import logger from '@helpers/logger'
import getCurrencyCode from '@i18n/getCurrencyCode'
import {useMutate} from '@packages/apollo-hooks'
import {v4 as uuidv4} from 'uuid'

import sendEcommerceEventMutation from './eventAnalytics/sendEcommerceEventMutation'
import trackAddToCart from './trackAddToCart'
import trackInitCheckout from './trackInitCheckout'
import trackPageView from './trackPageView'
import trackProductView from './trackProductView'
import trackPurchase from './trackPurchase'

export enum ECOMMERCE_EVENTS {
  PAGEVIEW = 'PageView',
  VIEWCONTENT = 'ViewContent',
  INITCHECKOUT = 'InitiateCheckout',
  ADDTOCART = 'AddToCart',
  PURCHASE = 'Purchase'
}

async function serverSendEvent(
  eventName: string,
  mutate: any,
  data: {
    currency: string
    eventId: string
    source: string
    totalPrice?: number
    items?: any[]
    orderId?: string
  }
) {
  try {
    if (!mutate) return
    const {orderId, eventId, source, items, currency, totalPrice} = data
    const variables = {
      eventName,
      eventIdentifier: eventId,
      currency,
      totalPrice,
      actionSource: source,
      origin: 'web',
      ...(items && {items}),
      ...(orderId && {orderId})
    }
    await mutate({
      mutation: sendEcommerceEventMutation,
      variables
    })
  } catch (error) {
    logger.warn('could not send event to ecommerceEvents', {error})
  }
}

export default function useTrackEcommerceEvent() {
  const mutate = useMutate()

  const trackEcommerceEvent = async (
    event: ECOMMERCE_EVENTS,
    params: {product?: any; amount?: number; item?: any; order?: any; cart?: any}
  ): Promise<void> => {
    try {
      function getEventServerData(
        event: ECOMMERCE_EVENTS,
        params: {product?: any; amount?: number; item?: any; order?: any; cart?: any}
      ): {
        currency: string
        eventId: string
        source: string
        totalPrice?: number
        items?: any[]
        orderId?: string
      } | null {
        const eventId = uuidv4()
        const currency = getCurrencyCode()
        const baseData = {currency, eventId, source: location.origin}

        if (event === ECOMMERCE_EVENTS.PAGEVIEW) {
          trackPageView(params)
          return null
        }
        if (event === ECOMMERCE_EVENTS.VIEWCONTENT) {
          trackProductView(params, {eventId})
          const {product} = params

          const items = [
            {id: product._id, quantity: 1, itemPrice: product.value, itemName: product.name}
          ]

          return {...baseData, items, totalPrice: product.value}
        }
        if (event === ECOMMERCE_EVENTS.INITCHECKOUT) {
          trackInitCheckout(params, {eventId})
          const {cart} = params

          return {
            ...baseData,
            items: Array.from(
              cart.items.map(item => {
                return {
                  id: item.product._id,
                  quantity: item.amount,
                  itemPrice: item.unitPrice,
                  itemName: item.product.name
                }
              })
            ),
            totalPrice: cart.itemsPrice
          }
        }
        if (event === ECOMMERCE_EVENTS.ADDTOCART) {
          trackAddToCart(params, {eventId})
          const {product, amount, item} = params

          const items = [
            {id: product._id, quantity: amount, itemPrice: item.unitPrice, itemName: product.name}
          ]
          return {...baseData, items, totalPrice: item.totalPrice}
        }
        if (event === ECOMMERCE_EVENTS.PURCHASE) {
          trackPurchase(params, {eventId})
          const {order} = params

          return {
            ...baseData,
            orderId: order._id,
            totalPrice: order.totalPrice,
            ...(order.items && {
              items: Array.from(
                order.items.map(item => {
                  return {
                    id: item.product._id,
                    quantity: item.amount,
                    itemPrice: item.unitPrice,
                    itemName: item.product.name
                  }
                })
              )
            })
          }
        }
        return null
      }
      const serverData = getEventServerData(event, params)
      if (!serverData) return
      await serverSendEvent(event, mutate, serverData)
    } catch (error) {
      logger.warn('error useTrackEcommerceEvent', {error})
    }
  }

  return trackEcommerceEvent
}
